import { useEffect, useRef } from 'react';

/**
 * Hook for "close on click-outside / Escape / ancestor-scroll" — the
 * three signals every popover and floating menu needs.
 *
 * Previously the same effect was copy-pasted across MessageContextMenu,
 * GifPickerOverlay, ProfilePopoverTrigger, GuildMuteMenu — each with
 * subtle differences in deps array, which caused listener-churn bugs
 * (the deps usually included `[props]`, re-binding every parent render).
 *
 * Usage:
 *
 *     const ref = useRef<HTMLDivElement>(null);
 *     useDismissable(open, ref, onClose);
 *
 * - `open`: bool; effect is a no-op when false.
 * - `ref`:  ref to the popover root; mousedown outside this element fires onClose.
 * - `onClose`: latest closure is captured fresh on every render via a ref,
 *   so the listener installs ONCE per open-cycle (not per parent render).
 *
 * Optional `options.closeOnScroll = false` opts out of the scroll-close —
 * useful when the popover is positioned by Radix / Portal with its own
 * anchor logic and a scroll-dismiss would hurt UX.
 */
export function useDismissable<T extends HTMLElement>(
  open: boolean,
  ref: React.RefObject<T | null>,
  onClose: () => void,
  options: { closeOnScroll?: boolean } = {},
): void {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const closeOnScroll = options.closeOnScroll ?? true;

  useEffect(() => {
    if (!open) return;
    function onDocPointer(e: MouseEvent) {
      const el = ref.current;
      if (!el) return;
      if (e.target instanceof Node && !el.contains(e.target)) {
        onCloseRef.current();
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onCloseRef.current();
    }
    function onScroll() {
      onCloseRef.current();
    }
    document.addEventListener('mousedown', onDocPointer);
    document.addEventListener('keydown', onKey);
    if (closeOnScroll) {
      // Capture phase + passive so we get scroll on every ancestor
      // (the popover is absolute, so its trigger moves while it stays
      // anchored to the viewport — close-on-scroll prevents the drift).
      document.addEventListener('scroll', onScroll, {
        capture: true,
        passive: true,
      });
    }
    return () => {
      document.removeEventListener('mousedown', onDocPointer);
      document.removeEventListener('keydown', onKey);
      if (closeOnScroll) {
        document.removeEventListener('scroll', onScroll, { capture: true });
      }
    };
  }, [open, ref, closeOnScroll]);
}
